import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { useWide } from '../shell/hooks';

// What a ▾ button opens: a menu right under the button on a laptop, a sheet from the bottom
// on a phone (decisions doc › Move ▾ menu). Escape, a tap outside or ✕ closes it.

export function Popover({
  open,
  onClose,
  label,
  children,
  sheetTitle,
  full,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  children: ComponentChildren;
  sheetTitle?: string;
  /** On a phone, the sheet fills the screen (the Filters panel). */
  full?: boolean;
}) {
  const wide = useWide();
  const box = useRef<HTMLDivElement>(null);

  // A layout effect: listening starts the moment it's drawn, so a quick Escape isn't missed.
  useLayoutEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const away = (e: Event) => {
      const host = box.current?.parentElement;
      if (wide && host && !host.contains(e.target as Node)) onClose();
    };
    // A menu opened from a button near the right edge lines up with the button's right side, so it
    // never runs off the screen (check-up: Reference's Jump to ▾, Nov 2).
    const el = box.current;
    if (wide && el) {
      el.classList.remove('end');
      if (el.getBoundingClientRect().right > document.documentElement.clientWidth - 8) el.classList.add('end');
    }
    document.addEventListener('keydown', esc);
    document.addEventListener('pointerdown', away);
    (box.current?.querySelector('[role="menuitem"], button, input') as HTMLElement | null)?.focus();
    return () => {
      document.removeEventListener('keydown', esc);
      document.removeEventListener('pointerdown', away);
    };
  }, [open, wide]);

  if (!open) return null;
  if (wide) {
    return (
      <div class="pop" ref={box} role="dialog" aria-label={label}>
        {children}
      </div>
    );
  }
  // On a phone the sheet opens at the page's top level, so a bar drawn in its own layer (the
  // Queue's Select several bar, pinned at the bottom) can't hold it behind the section bar (Kristi, Oct 29).
  const sheet = (
    <>
      <button type="button" class="sheet-scrim" aria-label="Close" tabIndex={-1} onClick={onClose} />
      <div class={`sheet${full ? ' full' : ''}`} ref={box} role="dialog" aria-label={label}>
        <div class="sheet-head">
          <span class="sheet-title">{sheetTitle ?? label}</span>
          <button type="button" class="round-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </>
  );
  return typeof document !== 'undefined' ? createPortal(sheet, document.body) : sheet;
}
